// Month-grid math. Uses YYYY-MM strings for months and YYYY-MM-DD for days.
// Monday-start week (Swiss / European convention).

import { formatYMD, parseYMDToUTC } from '@baerenfang/shared';

const DAY_MS = 86_400_000;

export function monthKey(date: Date | string): string {
  if (typeof date === 'string') return date.slice(0, 7);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function addMonthsToKey(monthYM: string, delta: number): string {
  const [y, m] = monthYM.split('-').map(Number);
  const totalMonths = (y! - 1) * 12 + (m! - 1) + delta;
  const ny = Math.floor(totalMonths / 12) + 1;
  const nm = (totalMonths % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

export function firstOfMonth(monthYM: string): string {
  return `${monthYM}-01`;
}

export function lastOfMonth(monthYM: string): string {
  const [y, m] = monthYM.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return `${monthYM}-${String(lastDay).padStart(2, '0')}`;
}

/** 42-cell grid (6 weeks × 7 days) starting on Monday. */
export function gridDates(monthYM: string): { date: string; inMonth: boolean }[] {
  const first = parseYMDToUTC(firstOfMonth(monthYM));
  const firstDow = new Date(first).getUTCDay(); // 0=Sun..6=Sat
  // Monday-start offset: 0 = Mon ... 6 = Sun
  const offset = (firstDow + 6) % 7;
  const start = first - offset * DAY_MS;
  const cells: { date: string; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const ms = start + i * DAY_MS;
    const date = formatYMD(ms);
    cells.push({ date, inMonth: date.startsWith(monthYM) });
  }
  return cells;
}

export function isValidMonthKey(s: string | null | undefined): s is string {
  return !!s && /^\d{4}-(0[1-9]|1[0-2])$/.test(s);
}

/** Clamp a month key into [minMonth, maxMonth] inclusive. */
export function clampMonth(monthYM: string, minMonth: string, maxMonth: string): string {
  if (monthYM < minMonth) return minMonth;
  if (monthYM > maxMonth) return maxMonth;
  return monthYM;
}
